import { useMemo } from "react"
import { useDebouncedValue } from "@mantine/hooks"
import useSWR from "swr"
import { PricingApi } from "../../apis"

const DEBOUNCE_MS = 300

type LineKey = { variantId: string; quantity: number }

/**
 * Debounced SWR-backed hook for fetching line prices from the price service.
 * Returns a { variantId -> finalPrice } map plus an isLoading flag.
 *
 * Pass businessId=undefined (or an empty lines array) to disable fetching.
 */
export function useVariantPricing(
  businessId: string | undefined,
  buyerBusinessId: string | undefined | null,
  lines: LineKey[],
) {
  const signature = useMemo(() => JSON.stringify(lines), [lines])
  const [debouncedSignature] = useDebouncedValue(signature, DEBOUNCE_MS)

  const key =
    businessId && lines.length > 0
      ? `swr.pricing.batch.${businessId}.${buyerBusinessId ?? "null"}.${debouncedSignature}`
      : null

  const { data, isValidating } = useSWR(
    key,
    () =>
      PricingApi.calculateBatch({
        businessId: businessId!,
        buyerBusinessId: buyerBusinessId ?? null,
        variants: lines,
      }),
    { revalidateOnFocus: false, revalidateOnReconnect: true, keepPreviousData: true },
  )

  const priceByVariant = useMemo(() => {
    const map: Record<string, number> = {}
    for (const pb of data || []) map[pb.variantId] = pb.finalPrice
    return map
  }, [data])

  return { priceByVariant, isLoading: isValidating }
}
